import { ActionEvents } from '@griply/types';
import { useGoals } from 'features/goals';
import { useCallback, useEffect, useRef } from 'react';
import { useDeleteGoalsMutation } from 'shared/hooks/queries/use-delete-goals-mutation';
import { useDeleteFavorites } from 'shared/hooks/use-delete-favorites';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { Goal } from 'shared/types/goal';
import { getSubGoals } from 'shared/utils/get-sub-goals';

export const useDeleteGoal = () => {
  const deleteGoalRef = useRef<Goal>();
  const goals = useGoals();
  const links = useEntityLinks();

  const track = useTrackEvents();
  const { mutate, isSuccess, reset, error, isPending } =
    useDeleteGoalsMutation();
  const removeFavorite = useDeleteFavorites();

  const submit = useCallback(
    (goal: Goal) => {
      deleteGoalRef.current = goal;
      const subGoals = getSubGoals(goal.id, {
        goals,
        recursive: true,
        links,
      }).map(({ id }) => id);
      mutate([goal.id, ...subGoals]);
      removeFavorite([goal.id, ...subGoals]);
    },
    [goals, links, mutate, removeFavorite],
  );

  const retry = useCallback(() => {
    const goal = deleteGoalRef.current;
    if (goal) {
      const subGoals = getSubGoals(goal.id, {
        goals,
        recursive: true,
        links,
      }).map(({ id }) => id);
      mutate([goal.id, ...subGoals]);
      removeFavorite([goal.id, ...subGoals]);
    }
  }, [goals, links, mutate, removeFavorite]);

  const resetAll = useCallback(() => {
    reset();
    deleteGoalRef.current = undefined;
  }, [reset]);

  useEffect(() => {
    if (isSuccess) {
      track(ActionEvents.GoalDelete, undefined);
    }
  }, [isSuccess, track]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
